import React from 'react';
import type {TFunction} from 'i18next';
import {
    AccountBalanceOutlined,
    AssignmentOutlined,
    BugReportOutlined,
    CardGiftcardOutlined,
    DashboardCustomizeOutlined,
    DevicesOutlined,
    FactoryOutlined,
    Groups2Rounded,
    Inventory2Outlined,
    ListAltRounded,
    LocalShippingOutlined,
    LoyaltyOutlined,
    MonetizationOnOutlined,
    NewspaperOutlined,
    NotificationImportantOutlined,
    PersonOutlined,
    PrintOutlined,
    ReceiptLongOutlined,
    ReceiptOutlined,
    Search,
    SettingsOutlined,
    ShieldOutlined,
    SupportAgentOutlined,
    VerifiedOutlined,
    WarehouseOutlined,
} from '@mui/icons-material';
import {routes, routesLists} from '../../../core/models/static/routes/app-routes';
import type {AccessLevelPackagesWithAggregates} from '../../../core/services/access-levels';
import type {PanelEntry} from '../types';
import ContactUsMessagesUnreadBadge from '../../views/panel/operator/support/contact-us-messages/contact-us-messages-unread-badge';

export interface CreateOperatorPanelEntriesDeps {
    /** `useTranslation('components', {keyPrefix: 'panel-sections.sidebar.default'})` */
    t: TFunction<'components'>;
    /** `useTranslation('components', {keyPrefix: 'panel-sections.command-search.pages'})` */
    tCmd: TFunction<'components'>;
    packages: AccessLevelPackagesWithAggregates;
    purchasingKeyForPlanning?: string | null;
}

/**
 * Builds the shared panel entry catalog for the default (operator) sidebar / command search surfaces.
 * Mirrors `panel-sections/sidebar/default-content` — every `hide` condition below preserves the
 * original access-level check exactly.
 */
export const createOperatorPanelEntries = ({
                                               t,
                                               tCmd,
                                               packages,
                                               purchasingKeyForPlanning,
                                           }: CreateOperatorPanelEntriesDeps): PanelEntry[] => [
    {
        id: 'lookup',
        title: t(t => t.lookup),
        sidebarTitle: t(t => t.lookup),
        icon: <Search/>,
        surfaces: ['sidebar'],
        sidebar: {path: routes.lookup.base},
        hide: () => !packages.lookup.accessToMenuItems,
        children: [
            {
                id: 'lookup-nexus-search',
                title: tCmd(t => t.nexusSearch),
                sidebarTitle: t(t => t.nexusSearch),
                subtitle: t(t => t.lookup),
                surfaces: ['sidebar', 'command-search'],
                to: routes.lookup.search,
                sidebar: {path: [routes.lookup.search, routes.lookup.productInformation]},
                accessKey: 'lookup.nexusSearch',
                hide: () => !packages.lookup.nexusSearch,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'lookup-comparison-list',
                title: tCmd(t => t.comparisonList),
                sidebarTitle: t(t => t.comparisonList),
                subtitle: t(t => t.lookup),
                surfaces: ['sidebar', 'command-search'],
                to: routes.lookup.compare,
                accessKey: 'lookup.comparisonList',
                hide: () => !packages.lookup.comparisonList,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'lookup-competitor-price-reports',
                title: tCmd(t => t.competitorReports),
                sidebarTitle: t(t => t.competitorReports),
                subtitle: t(t => t.lookup),
                surfaces: ['sidebar', 'command-search'],
                to: routes.lookup.competitorPriceReport,
                accessKey: 'lookup.viewAllCompetitorPriceReports',
                hide: () => !(
                    packages.lookup.viewAllCompetitorPriceReports
                    || packages.lookup.viewMyCompetitorPriceReports
                ),
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
    {
        id: 'warranty-return',
        title: t(t => t.warrantyReturn),
        sidebarTitle: t(t => t.warrantyReturn),
        icon: <VerifiedOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: [routes.warrantyAndReturn.base, routes.drivers]},
        hide: () => !packages.warrantyAndReturn.accessToMenuItems,
        children: [
            {
                id: 'warranty-requests',
                title: tCmd(t => t.warrantyRequests),
                sidebarTitle: t(t => t.requests),
                subtitle: t(t => t.warrantyReturn),
                surfaces: ['sidebar', 'command-search'],
                to: routes.warrantyAndReturn.requests,
                sidebar: {path: [routes.warrantyAndReturn.requests, routes.warrantyAndReturn.single]},
                accessKey: 'warrantyAndReturn.viewList',
                hide: () => !packages.warrantyAndReturn.viewList,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'warranty-status-settings',
                title: tCmd(t => t.warrantyStatusSettings),
                sidebarTitle: t(t => t.statusSettings),
                subtitle: t(t => t.warrantyReturn),
                surfaces: ['sidebar', 'command-search'],
                to: routes.warrantyAndReturn.settings.statuses,
                accessKey: 'warrantyAndReturn.editConfigurations',
                hide: () => !packages.warrantyAndReturn.editConfigurations,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'warranty-process-settings',
                title: tCmd(t => t.warrantyProcessSettings),
                sidebarTitle: t(t => t.processSettings),
                subtitle: t(t => t.warrantyReturn),
                surfaces: ['sidebar', 'command-search'],
                to: routes.warrantyAndReturn.settings.process,
                accessKey: 'warrantyAndReturn.editConfigurations',
                hide: () => !packages.warrantyAndReturn.editConfigurations,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'warranty-drivers',
                title: tCmd(t => t.warrantyDrivers),
                sidebarTitle: t(t => t.drivers),
                subtitle: t(t => t.warrantyReturn),
                surfaces: ['sidebar', 'command-search'],
                to: routes.drivers,
                accessKey: 'warrantyAndReturn.drivers',
                hide: () => !packages.warrantyAndReturn.drivers,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
    {
        id: 'customers',
        title: t(t => t.customers),
        sidebarTitle: t(t => t.customers),
        icon: <PersonOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: [routes.customers.base]},
        hide: () => !(packages.base.customerList || packages.base.customerMembersList),
        children: [
            {
                id: 'customers-list',
                title: tCmd(t => t.customerList),
                sidebarTitle: t(t => t.customerList),
                subtitle: t(t => t.customers),
                surfaces: ['sidebar', 'command-search'],
                to: routes.customers.list,
                accessKey: 'base.customerList',
                hide: () => !packages.base.customerList,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'customers-members',
                title: tCmd(t => t.members),
                sidebarTitle: t(t => t.members),
                subtitle: t(t => t.customers),
                surfaces: ['sidebar', 'command-search'],
                to: routes.customers.members.base,
                accessKey: 'base.customerMembersList',
                hide: () => !packages.base.customerMembersList,
                keywords: ['active members', 'inactive members', 'passive members', 'members', 'onhold'],
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
                sidebar: {path: routes.customers.members.base},
            },
        ],
    },
    {
        id: 'operators',
        title: t(t => t.operators),
        sidebarTitle: t(t => t.operators),
        icon: <Groups2Rounded/>,
        surfaces: ['sidebar'],
        sidebar: {path: routes.operators.base},
        hide: () => !(packages.base.operatorsList || packages.base.manageOperators),
        children: [
            {
                id: 'operators-list',
                title: tCmd(t => t.operatorsList),
                sidebarTitle: t(t => t.operatorsList),
                subtitle: t(t => t.operators),
                surfaces: ['sidebar', 'command-search'],
                to: routes.operators.list,
                accessKey: 'base.operatorsList',
                hide: () => !packages.base.operatorsList,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'operators-groups',
                title: tCmd(t => t.operatorGroups),
                sidebarTitle: t(t => t.operatorGroups),
                subtitle: t(t => t.operators),
                surfaces: ['sidebar', 'command-search'],
                to: routes.operators.groups.base,
                sidebar: {path: [routes.operators.groups.base, routes.operators.groups.single]},
                accessKey: 'base.manageOperators',
                hide: () => !packages.base.manageOperators,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'operators-dashboards',
                title: tCmd(t => t.operatorDashboards),
                sidebarTitle: t(t => t.operatorsDashboards),
                subtitle: t(t => t.operators),
                surfaces: ['sidebar', 'command-search'],
                to: routes.operators.dashboards.base,
                sidebar: {path: [routes.operators.dashboards.base, routes.operators.dashboards.single]},
                hide: () => !(packages.base.manageOperators && packages.dashboard.systemWideDashboards),
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
    {
        id: 'vendors',
        title: tCmd(t => t.vendorsList),
        sidebarTitle: t(t => t.vendors),
        icon: <FactoryOutlined/>,
        surfaces: ['sidebar', 'command-search'],
        to: routes.vendors,
        accessKey: 'base.viewVendors',
        hide: () => !packages.base.viewVendors,
        commandSearch: {providerId: 'navigation-general', category: 'pages'},
    },
    {
        id: 'sales',
        title: t(t => t.sales),
        sidebarTitle: t(t => t.sales),
        icon: <MonetizationOnOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: [routes.sales.base, routes.salesPerson.base, routes.sales.promotions.base]},
        hide: () => !packages.sales.accessToMenuItems,
        children: [
            {
                id: 'sales-person',
                title: tCmd(t => t.salesPerson),
                sidebarTitle: t(t => t.salesPerson),
                subtitle: t(t => t.sales),
                surfaces: ['sidebar', 'command-search'],
                to: routes.salesPerson.base,
                accessKey: 'sales.salesPerson',
                hide: () => !packages.sales.salesPerson,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'sales-quotes',
                title: tCmd(t => t.salesQuotes),
                sidebarTitle: t(t => t.quotes),
                subtitle: t(t => t.sales),
                surfaces: ['sidebar', 'command-search'],
                to: routes.sales.quotes,
                accessKey: 'sales.quotesList',
                hide: () => !packages.sales.quotesList,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'sales-orders',
                title: tCmd(t => t.salesOrders),
                sidebarTitle: t(t => t.salesOrders),
                subtitle: t(t => t.sales),
                surfaces: ['sidebar', 'command-search'],
                to: routes.sales.salesOrder,
                accessKey: 'sales.salesOrdersList',
                hide: () => !packages.sales.salesOrdersList,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'sales-back-orders',
                title: tCmd(t => t.salesBackOrders),
                sidebarTitle: t(t => t.backOrders),
                subtitle: t(t => t.sales),
                surfaces: ['sidebar', 'command-search'],
                to: routes.sales.backOrder,
                accessKey: 'sales.salesOrdersList',
                hide: () => !packages.sales.salesOrdersList,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'sales-invoices',
                title: tCmd(t => t.salesInvoices),
                sidebarTitle: t(t => t.salesInvoices),
                subtitle: t(t => t.sales),
                surfaces: ['sidebar', 'command-search'],
                to: routes.sales.invoices,
                accessKey: 'sales.invoicesList',
                hide: () => !packages.sales.invoicesList,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'sales-credits',
                title: tCmd(t => t.salesCredits),
                sidebarTitle: t(t => t.credits),
                subtitle: t(t => t.sales),
                surfaces: ['sidebar', 'command-search'],
                to: routes.sales.credits,
                accessKey: 'sales.creditsList',
                hide: () => !packages.sales.creditsList,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'sales-promotions',
                title: tCmd(t => t.salesPromotions),
                sidebarTitle: t(t => t.promotions),
                subtitle: t(t => t.sales),
                surfaces: ['sidebar', 'command-search'],
                to: routes.sales.promotions.list,
                sidebar: {path: routes.sales.promotions.base},
                accessKey: 'sales.promotions',
                hide: () => !packages.sales.promotions,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
    {
        id: 'accounting',
        title: t(t => t.accounting),
        sidebarTitle: t(t => t.accounting),
        icon: <AccountBalanceOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: routes.accounting.base},
        hide: () => !(packages.sales.invoicesList || packages.sales.accountReceivables),
        children: [
            {
                id: 'accounting-invoices',
                title: tCmd(t => t.accountingInvoices),
                sidebarTitle: t(t => t.accountingInvoices),
                subtitle: t(t => t.accounting),
                surfaces: ['sidebar', 'command-search'],
                to: routes.accounting.invoices,
                accessKey: 'sales.invoicesList',
                hide: () => !packages.sales.invoicesList,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'accounting-account-receivable',
                title: tCmd(t => t.accountReceivable),
                sidebarTitle: t(t => t.accountReceivable),
                subtitle: t(t => t.accounting),
                surfaces: ['sidebar', 'command-search'],
                to: routes.accounting.accountReceivable,
                accessKey: 'sales.accountReceivables',
                hide: () => !packages.sales.accountReceivables,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'accounting-transaction-history',
                title: tCmd(t => t.transactionHistory),
                sidebarTitle: t(t => t.transactionHistory),
                subtitle: t(t => t.accounting),
                surfaces: ['sidebar', 'command-search'],
                to: routes.accounting.onlinePaymentTransactions,
                accessKey: 'sales.accountReceivables',
                hide: () => !packages.sales.accountReceivables,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
    {
        id: 'purchase',
        title: t(t => t.purchase),
        sidebarTitle: t(t => t.purchase),
        icon: <ReceiptOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: routesLists.purchase},
        hide: () => !packages.purchasing.accessToMenuItemsWithParams(purchasingKeyForPlanning),
        children: [
            {
                id: 'purchase-orders',
                title: tCmd(t => t.purchaseOrders),
                sidebarTitle: t(t => t.purchaseOrders),
                subtitle: t(t => t.purchase),
                surfaces: ['sidebar', 'command-search'],
                to: routes.purchase.orders,
                sidebar: {path: routes.purchase.orders},
                accessKey: 'purchasing.viewPurchaseOrders',
                hide: () => !packages.purchasing.viewPurchaseOrders,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'purchase-history',
                title: tCmd(t => t.purchaseHistory),
                sidebarTitle: t(t => t.purchaseHistory),
                subtitle: t(t => t.purchase),
                surfaces: ['sidebar', 'command-search'],
                to: routes.purchase.history,
                sidebar: {path: routes.purchase.history},
                accessKey: 'purchasing.viewPurchaseHistory',
                hide: () => !packages.purchasing.viewPurchaseHistory,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'purchase-planning',
                title: tCmd(t => t.purchasePlanning),
                sidebarTitle: t(t => t.purchasePlanning),
                subtitle: t(t => t.purchase),
                surfaces: ['sidebar', 'command-search'],
                resolveTo: () => purchasingKeyForPlanning ? routes.purchase.planningList : undefined,
                sidebar: {path: [routes.purchase.planningList, routes.purchase.planningDetails]},
                hide: () => !packages.purchasing.viewPurchasePlanning || !purchasingKeyForPlanning,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
    {
        id: 'freight',
        title: t(t => t.purchaseFreight),
        sidebarTitle: t(t => t.purchaseFreight),
        icon: <LocalShippingOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: routes.shippingFees.base},
        hide: () => !(
            packages.sales.freightFees ||
            packages.sales.freeFreightFees ||
            packages.sales.freightCompanies ||
            packages.sales.freightReports ||
            packages.sales.regionRates ||
            packages.sales.partRates
        ),
        children: [
            {
                id: 'freight-shipping-fees',
                title: tCmd(t => t.shippingFees),
                sidebarTitle: t(t => t.shippingFees),
                subtitle: t(t => t.purchaseFreight),
                surfaces: ['sidebar', 'command-search'],
                to: routes.shippingFees.list,
                accessKey: 'sales.freightFees',
                hide: () => !packages.sales.freightFees,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'freight-free-shipping-fees',
                title: tCmd(t => t.freeShippingFees),
                sidebarTitle: t(t => t.freeShippingFess),
                subtitle: t(t => t.purchaseFreight),
                surfaces: ['sidebar', 'command-search'],
                to: routes.shippingFees.settings,
                accessKey: 'sales.freeFreightFees',
                hide: () => !packages.sales.freeFreightFees,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'freight-manage-companies',
                title: tCmd(t => t.manageShippingFeesCompanies),
                sidebarTitle: t(t => t.manageShippingFeesCompanies),
                subtitle: t(t => t.purchaseFreight),
                surfaces: ['sidebar', 'command-search'],
                to: routes.shippingFees.companies,
                accessKey: 'sales.freightCompanies',
                hide: () => !packages.sales.freightCompanies,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'freight-reports',
                title: tCmd(t => t.shippingFeesReports),
                sidebarTitle: t(t => t.shippingFeesReports),
                subtitle: t(t => t.purchaseFreight),
                surfaces: ['sidebar', 'command-search'],
                to: routes.shippingFees.reports,
                accessKey: 'sales.freightReports',
                hide: () => !packages.sales.freightReports,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'freight-shipping-rates',
                title: tCmd(t => t.regionRates),
                sidebarTitle: t(t => t.regionRates),
                subtitle: t(t => t.purchaseFreight),
                surfaces: ['sidebar', 'command-search'],
                to: routes.shippingFees.shippingRates.base,
                hide: () => !(packages.sales.regionRates || packages.sales.partRates),
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
    {
        id: 'settings',
        title: t(t => t.settings),
        sidebarTitle: t(t => t.settings),
        icon: <SettingsOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: routes.settings.base},
        hide: () => !(
            packages.base.organizationSettings ||
            packages.base.personalSettings ||
            packages.base.partnershipSettings ||
            packages.base.partnersManagementSettings ||
            packages.sales.viewPriceMatrices ||
            packages.base.manageRegions ||
            packages.base.manageTaxes ||
            packages.base.manageTerritories ||
            packages.lookup.manageIndustries ||
            packages.base.importing ||
            packages.sales.exportInvoicesList ||
            packages.base.exportSalesTax ||
            packages.sales.exportPriceMatrices ||
            packages.sales.exportTerritoriesAndAccountManagers ||
            packages.lookup.exportSystemProductsBasicInformation ||
            packages.sales.giftCards ||
            packages.sales.managePriceLevels
        ),
        children: [
            {
                id: 'settings-organization',
                title: tCmd(t => t.systemSettings),
                sidebarTitle: t(t => t.systemSettings),
                subtitle: t(t => t.settings),
                surfaces: ['sidebar', 'command-search'],
                to: routes.settings.organization,
                sidebar: {
                    path: [
                        routes.settings.organization,
                        routes.settings.personal,
                        routes.settings.regions,
                        routes.settings.taxes.list,
                        routes.settings.taxes.regionTaxes,
                        routes.settings.territories,
                        routes.settings.industries,
                        routes.settings.priceLevels,
                        routes.settings.salesPersons,
                    ],
                },
                hide: () => !(
                    packages.base.organizationSettings ||
                    packages.base.personalSettings ||
                    packages.base.manageRegions ||
                    packages.base.manageTaxes ||
                    packages.base.manageTerritories ||
                    packages.lookup.manageIndustries ||
                    packages.sales.managePriceLevels
                ),
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'settings-tier-pricing',
                title: tCmd(t => t.tierPricing),
                sidebarTitle: t(t => t.tierPricing),
                subtitle: t(t => t.settings),
                surfaces: ['sidebar', 'command-search'],
                to: routes.settings.priceMatrix.list,
                sidebar: {path: routes.settings.priceMatrix.base},
                accessKey: 'sales.viewPriceMatrices',
                hide: () => !packages.sales.viewPriceMatrices,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'settings-exporting',
                title: tCmd(t => t.exporting),
                sidebarTitle: t(t => t.exporting),
                subtitle: t(t => t.settings),
                surfaces: ['sidebar', 'command-search'],
                to: routes.settings.export,
                accessKey: 'sales.exportInvoicesList',
                hide: () => !(
                    packages.sales.exportInvoicesList ||
                    packages.base.exportSalesTax ||
                    packages.sales.exportPriceMatrices ||
                    packages.sales.exportTerritoriesAndAccountManagers ||
                    packages.lookup.exportSystemProductsBasicInformation
                ),
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'settings-importing',
                title: tCmd(t => t.importing),
                sidebarTitle: t(t => t.importing),
                subtitle: t(t => t.settings),
                surfaces: ['sidebar', 'command-search'],
                to: routes.settings.import,
                accessKey: 'base.importing',
                hide: () => !packages.base.importing,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'settings-partnership',
                title: tCmd(t => t.partnership),
                sidebarTitle: t(t => t.partnership),
                subtitle: t(t => t.settings),
                surfaces: ['sidebar', 'command-search'],
                to: routes.settings.partnership,
                accessKey: 'base.partnershipSettings',
                hide: () => !packages.base.partnershipSettings,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'settings-partners-management',
                title: tCmd(t => t.partnersManagement),
                sidebarTitle: t(t => t.partnersManagement),
                subtitle: t(t => t.settings),
                surfaces: ['sidebar', 'command-search'],
                to: routes.settings.partnersManagement,
                accessKey: 'base.partnersManagementSettings',
                hide: () => !packages.base.partnersManagementSettings,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'settings-gift-cards',
                title: t(t => t.giftCards),
                sidebarTitle: t(t => t.giftCards),
                icon: <CardGiftcardOutlined/>,
                surfaces: ['sidebar'],
                sidebar: {path: routes.settings.giftCards.base},
                accessKey: 'sales.giftCards',
                hide: () => !packages.sales.giftCards,
                children: [
                    {
                        id: 'settings-gift-cards-list',
                        title: tCmd(t => t.giftCardsList),
                        sidebarTitle: t(t => t.giftCardsList),
                        subtitle: t(t => t.giftCards),
                        surfaces: ['sidebar', 'command-search'],
                        to: routes.settings.giftCards.list,
                        accessKey: 'sales.giftCards',
                        hide: () => !packages.sales.giftCards,
                        commandSearch: {providerId: 'navigation-general', category: 'pages'},
                    },
                    {
                        id: 'settings-gift-cards-categories',
                        title: tCmd(t => t.giftCardsCategories),
                        sidebarTitle: t(t => t.giftCardsCategories),
                        subtitle: t(t => t.giftCards),
                        surfaces: ['sidebar', 'command-search'],
                        to: routes.settings.giftCards.categories,
                        accessKey: 'sales.manageGiftCards',
                        hide: () => !packages.sales.manageGiftCards,
                        commandSearch: {providerId: 'navigation-general', category: 'pages'},
                    },
                ],
            },
        ],
    },
    {
        id: 'notifications',
        title: t(t => t.notifications),
        sidebarTitle: t(t => t.notifications),
        icon: <NotificationImportantOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: routes.userNotifications.base},
        hide: () => !packages.notifications.accessToMenuItems,
        children: [
            {
                id: 'notifications-list',
                title: tCmd(t => t.notificationsList),
                sidebarTitle: t(t => t.notificationsList),
                subtitle: t(t => t.notifications),
                surfaces: ['sidebar', 'command-search'],
                to: routes.userNotifications.list,
                accessKey: 'notifications.customerNotifications',
                hide: () => !packages.notifications.customerNotifications,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'notifications-subscriptions',
                title: tCmd(t => t.notificationsSubscriptions),
                sidebarTitle: t(t => t.notificationsSubscriptions),
                subtitle: t(t => t.notifications),
                surfaces: ['sidebar', 'command-search'],
                to: routes.userNotifications.subscriptions,
                accessKey: 'notifications.manageCustomerNotifications',
                hide: () => !packages.notifications.manageCustomerNotifications,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'notifications-access-levels',
                title: tCmd(t => t.notificationsAccessLevels),
                sidebarTitle: t(t => t.notificationsAccessLevels),
                subtitle: t(t => t.notifications),
                surfaces: ['sidebar', 'command-search'],
                to: routes.userNotifications.accessLevels,
                accessKey: 'notifications.manageCustomerNotifications',
                hide: () => !packages.notifications.manageCustomerNotifications,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
    {
        id: 'news',
        title: t(t => t.news),
        sidebarTitle: t(t => t.news),
        icon: <NewspaperOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: routes.content.base},
        hide: () => !(packages.base.contentBlogs || packages.base.contentVideos),
        children: [
            {
                id: 'news-blogs',
                title: tCmd(t => t.blogs),
                sidebarTitle: t(t => t.blogs),
                subtitle: t(t => t.news),
                surfaces: ['sidebar', 'command-search'],
                to: routes.content.blogs.list,
                sidebar: {path: [routes.content.blogs.list, routes.content.blogs.single]},
                accessKey: 'base.contentBlogs',
                hide: () => !packages.base.contentBlogs,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'news-videos',
                title: tCmd(t => t.videos),
                sidebarTitle: t(t => t.videos),
                subtitle: t(t => t.news),
                surfaces: ['sidebar', 'command-search'],
                to: routes.content.videos.list,
                sidebar: {path: [routes.content.videos.list, routes.content.videos.single]},
                accessKey: 'base.contentVideos',
                hide: () => !packages.base.contentVideos,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
    {
        id: 'analytics',
        title: t(t => t.analytics),
        sidebarTitle: t(t => t.analytics),
        icon: <DashboardCustomizeOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: routesLists.analytics},
        hide: () => !packages.dashboard.accessToMenuItems,
        children: [
            {
                id: 'analytics-dashboards',
                title: tCmd(t => t.analyticsDashboards),
                sidebarTitle: t(t => t.dashboards),
                subtitle: t(t => t.analytics),
                surfaces: ['sidebar', 'command-search'],
                to: routes.analytics.dashboards,
                accessKey: 'dashboard.viewDashboards',
                hide: () => !packages.dashboard.viewDashboards,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'analytics-classic-dashboards',
                title: tCmd(t => t.classicDashboards),
                sidebarTitle: t(t => t.classicDashboards),
                subtitle: t(t => t.analytics),
                surfaces: ['sidebar', 'command-search'],
                to: routes.analytics.systemDashboards,
                accessKey: 'dashboard.systemWideDashboards',
                hide: () => !packages.dashboard.systemWideDashboards,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'analytics-classic-reports',
                title: tCmd(t => t.classicReports),
                sidebarTitle: t(t => t.classicReports),
                subtitle: t(t => t.analytics),
                surfaces: ['sidebar', 'command-search'],
                to: routes.analytics.systemReports,
                accessKey: 'dashboard.systemWideReports',
                hide: () => !packages.dashboard.systemWideReports,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'analytics-lookup-analytics',
                title: tCmd(t => t.lookupAnalytics),
                sidebarTitle: t(t => t.lookupAnalytics),
                subtitle: t(t => t.analytics),
                surfaces: ['sidebar', 'command-search'],
                to: routes.analytics.lookup.base,
                sidebar: {path: routesLists.analyticsLookup},
                accessKey: 'dashboard.lookupAnalytics',
                hide: () => !packages.dashboard.lookupAnalytics,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'analytics-lost-sales-analytics',
                title: tCmd(t => t.lostSalesAnalytics),
                sidebarTitle: t(t => t.lostSalesAnalytics),
                subtitle: t(t => t.analytics),
                surfaces: ['sidebar', 'command-search'],
                to: routes.analytics.lostSales.base,
                sidebar: {path: routesLists.analyticsLostSales},
                accessKey: 'dashboard.lostSalesAnalytics',
                hide: () => !packages.dashboard.lostSalesAnalytics,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'analytics-logs',
                title: t(t => t.logs),
                sidebarTitle: t(t => t.logs),
                icon: <ReceiptLongOutlined/>,
                surfaces: ['sidebar'],
                sidebar: {path: routes.analytics.logs.base},
                hide: () => !packages.dashboard.lookupLogs && !packages.dashboard.lostSalesLogs,
                children: [
                    {
                        id: 'analytics-logs-lookup',
                        title: tCmd(t => t.lookupLogs),
                        sidebarTitle: t(t => t.lookupLogs),
                        subtitle: t(t => t.analytics),
                        surfaces: ['sidebar', 'command-search'],
                        to: routes.analytics.logs.lookup,
                        accessKey: 'dashboard.lookupLogs',
                        hide: () => !packages.dashboard.lookupLogs,
                        commandSearch: {providerId: 'navigation-general', category: 'pages'},
                    },
                    {
                        id: 'analytics-logs-lost-sales',
                        title: tCmd(t => t.lostSalesLogs),
                        sidebarTitle: t(t => t.lostSalesLogs),
                        subtitle: t(t => t.analytics),
                        surfaces: ['sidebar', 'command-search'],
                        to: routes.analytics.logs.lostSales,
                        accessKey: 'dashboard.lostSalesLogs',
                        hide: () => !packages.dashboard.lostSalesLogs,
                        commandSearch: {providerId: 'navigation-general', category: 'pages'},
                    },
                ],
            },
        ],
    },
    {
        id: 'printing',
        title: t(t => t.printing),
        sidebarTitle: t(t => t.printing),
        icon: <PrintOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: routes.printing.base},
        hide: () => !packages.bizPrint.accessToMenuItems,
        children: [
            {
                id: 'printing-print-hub',
                title: tCmd(t => t.printHub),
                sidebarTitle: t(t => t.printHub),
                subtitle: t(t => t.printing),
                surfaces: ['sidebar', 'command-search'],
                to: routes.printing.printHub,
                accessKey: 'bizPrint.printHub',
                hide: () => !packages.bizPrint.printHub,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'printing-product-labels',
                title: tCmd(t => t.productLabels),
                sidebarTitle: t(t => t.productLabels),
                subtitle: t(t => t.printing),
                surfaces: ['sidebar', 'command-search'],
                to: routes.printing.printLabels,
                accessKey: 'bizPrint.printLabels',
                hide: () => !packages.bizPrint.printLabels,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'printing-product-catalogue',
                title: tCmd(t => t.productCatalogue),
                sidebarTitle: t(t => t.productCatalogue),
                subtitle: t(t => t.printing),
                surfaces: ['sidebar', 'command-search'],
                to: routes.printing.printCatalogue,
                accessKey: 'bizPrint.printCatalogue',
                hide: () => !packages.bizPrint.printCatalogue,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'printing-packing-list',
                title: tCmd(t => t.packingList),
                sidebarTitle: t(t => t.packingList),
                subtitle: t(t => t.printing),
                surfaces: ['sidebar', 'command-search'],
                to: routes.printing.printPackingList,
                accessKey: 'bizPrint.printPackingList',
                hide: () => !packages.bizPrint.printPackingList,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'printing-product-brochure',
                title: tCmd(t => t.productPoster),
                sidebarTitle: t(t => t.productPoster),
                subtitle: t(t => t.printing),
                surfaces: ['sidebar', 'command-search'],
                to: routes.printing.printBrochure,
                accessKey: 'bizPrint.printBrochure',
                hide: () => !packages.bizPrint.printBrochure,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
    {
        id: 'products',
        title: t(t => t.products),
        sidebarTitle: t(t => t.products),
        icon: <Inventory2Outlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: [routes.products.base, routes.products.bulkPriceChange.base]},
        hide: () => !(
            packages.lookup.products ||
            packages.lookup.manageTags ||
            packages.lookup.manageCategories ||
            packages.lookup.manageRelationTypes ||
            packages.lookup.manageCodeTypes ||
            packages.lookup.manageSharingRules ||
            packages.lookup.applicationSuggestionsList ||
            packages.lookup.technicalSuggestionsList ||
            packages.lookup.fastConnection ||
            packages.lookup.bulkPriceChanges
        ),
        children: [
            {
                id: 'products-list',
                title: tCmd(t => t.productsList),
                sidebarTitle: t(t => t.productsList),
                subtitle: t(t => t.products),
                surfaces: ['sidebar', 'command-search'],
                to: routes.products.list,
                sidebar: {path: [routes.products.list, routes.products.single]},
                accessKey: 'lookup.products',
                hide: () => !packages.lookup.products,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'products-manage-tags',
                title: tCmd(t => t.manageProductTags),
                sidebarTitle: t(t => t.manageProductTags),
                subtitle: t(t => t.products),
                surfaces: ['sidebar', 'command-search'],
                to: routes.products.tags.list,
                sidebar: {path: [routes.products.tags.list, routes.products.tags.single]},
                accessKey: 'lookup.manageTags',
                hide: () => !packages.lookup.manageTags,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'products-manage-properties',
                title: tCmd(t => t.manageProductProperties),
                sidebarTitle: t(t => t.manageProductProperties),
                subtitle: t(t => t.products),
                surfaces: ['sidebar', 'command-search'],
                to: routes.products.manageProperties,
                accessKey: 'lookup.manageProductProperties',
                hide: () => !packages.lookup.manageProductProperties,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'products-manage-categories',
                title: tCmd(t => t.manageProductCategories),
                sidebarTitle: t(t => t.manageProductCategories),
                subtitle: t(t => t.products),
                surfaces: ['sidebar', 'command-search'],
                to: routes.products.categoryManagement,
                accessKey: 'lookup.manageCategories',
                hide: () => !packages.lookup.manageCategories,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'products-relation-types',
                title: tCmd(t => t.productRelationTypes),
                sidebarTitle: t(t => t.productRelationTypes),
                subtitle: t(t => t.products),
                surfaces: ['sidebar', 'command-search'],
                to: routes.products.relationTypes,
                accessKey: 'lookup.manageRelationTypes',
                hide: () => !packages.lookup.manageRelationTypes,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'products-code-types',
                title: tCmd(t => t.productsCodeTypes),
                sidebarTitle: t(t => t.productsCodeTypes),
                subtitle: t(t => t.products),
                surfaces: ['sidebar', 'command-search'],
                to: routes.products.codeTypes,
                accessKey: 'lookup.manageCodeTypes',
                hide: () => !packages.lookup.manageCodeTypes,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'products-sharing-rules',
                title: tCmd(t => t.productsSharingRules),
                sidebarTitle: t(t => t.productsSharingRules),
                subtitle: t(t => t.products),
                surfaces: ['sidebar', 'command-search'],
                to: routes.products.shareRules,
                accessKey: 'lookup.manageSharingRules',
                hide: () => !packages.lookup.manageSharingRules,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'products-bulk-price-change',
                title: tCmd(t => t.bulkPriceChange),
                sidebarTitle: t(t => t.bulkPriceChange),
                subtitle: t(t => t.products),
                surfaces: ['sidebar', 'command-search'],
                to: routes.products.bulkPriceChange.dashboard,
                sidebar: {path: routes.products.bulkPriceChange.base},
                accessKey: 'lookup.bulkPriceChanges',
                hide: () => !packages.lookup.bulkPriceChanges,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'products-manage-suggestions',
                title: tCmd(t => t.manageProductSuggestions),
                sidebarTitle: t(t => t.manageProductSuggestions),
                subtitle: t(t => t.products),
                surfaces: ['sidebar', 'command-search'],
                to: routes.products.manageSuggestions.application,
                sidebar: {
                    path: [
                        routes.products.manageSuggestions.base,
                        routes.products.manageSuggestions.application,
                        routes.products.manageSuggestions.technical,
                    ],
                },
                hide: () => !(
                    packages.lookup.applicationSuggestionsList ||
                    packages.lookup.technicalSuggestionsList
                ),
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'products-fast-connection',
                title: tCmd(t => t.productsFastConnection),
                sidebarTitle: t(t => t.productsFastConnection),
                subtitle: t(t => t.products),
                surfaces: ['sidebar', 'command-search'],
                to: routes.products.productFastConnection,
                accessKey: 'lookup.fastConnection',
                hide: () => !packages.lookup.fastConnection,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
    {
        id: 'inventory',
        title: t(t => t.inventory),
        sidebarTitle: t(t => t.inventory),
        icon: <WarehouseOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: routes.inventory.base},
        hide: () => !packages.bizInventory.accessToMenuItems,
        children: [
            {
                id: 'inventory-adjustments',
                title: t(t => t.inventoryAdjustments),
                sidebarTitle: t(t => t.inventoryAdjustments),
                icon: <WarehouseOutlined/>,
                surfaces: ['sidebar'],
                sidebar: {path: routes.inventory.inventoryAdjustment.base},
                hide: () => !(
                    packages.bizInventory.adjustmentList ||
                    packages.bizInventory.adjustmentItems ||
                    packages.bizInventory.adjustmentReports
                ),
                children: [
                    {
                        id: 'inventory-adjustments-list',
                        title: tCmd(t => t.inventoryAdjustmentsList),
                        sidebarTitle: t(t => t.inventoryAdjustmentsList),
                        subtitle: t(t => t.inventory),
                        surfaces: ['sidebar', 'command-search'],
                        to: routes.inventory.inventoryAdjustment.list,
                        sidebar: {
                            path: [
                                routes.inventory.inventoryAdjustment.list,
                                routes.inventory.inventoryAdjustment.single,
                            ],
                        },
                        accessKey: 'bizInventory.adjustmentList',
                        hide: () => !packages.bizInventory.adjustmentList,
                        commandSearch: {providerId: 'navigation-general', category: 'pages'},
                    },
                    {
                        id: 'inventory-adjustments-items',
                        title: tCmd(t => t.inventoryAdjustmentsItems),
                        sidebarTitle: t(t => t.inventoryAdjustmentsItems),
                        subtitle: t(t => t.inventory),
                        surfaces: ['sidebar', 'command-search'],
                        to: routes.inventory.inventoryAdjustment.items,
                        accessKey: 'bizInventory.adjustmentItems',
                        hide: () => !packages.bizInventory.adjustmentItems,
                        commandSearch: {providerId: 'navigation-general', category: 'pages'},
                    },
                    {
                        id: 'inventory-adjustments-report',
                        title: tCmd(t => t.inventoryAdjustmentsReport),
                        sidebarTitle: t(t => t.inventoryAdjustmentsReport),
                        subtitle: t(t => t.inventory),
                        surfaces: ['sidebar', 'command-search'],
                        to: routes.inventory.inventoryAdjustment.report,
                        accessKey: 'bizInventory.adjustmentReports',
                        hide: () => !packages.bizInventory.adjustmentReports,
                        commandSearch: {providerId: 'navigation-general', category: 'pages'},
                    },
                ],
            },
            {
                id: 'inventory-transfer-list',
                title: tCmd(t => t.transferList),
                sidebarTitle: t(t => t.transferList),
                subtitle: t(t => t.inventory),
                surfaces: ['sidebar', 'command-search'],
                to: routes.inventory.inventoryTransfer.list,
                sidebar: {
                    path: [
                        routes.inventory.inventoryTransfer.list,
                        routes.inventory.inventoryTransfer.single,
                        // legacy
                        routes.inventory.inventoryTransfer.historyList,
                        routes.inventory.inventoryTransfer.batchInformation,
                        routes.inventory.inventoryTransfer.historyInformation,
                    ],
                },
                accessKey: 'bizInventory.transferHistoryList',
                hide: () => !packages.bizInventory.transferHistoryList,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'inventory-availability-thresholds',
                title: tCmd(t => t.inventoryAvailabilityThresholds),
                sidebarTitle: t(t => t.inventoryAvailabilityThresholds),
                subtitle: t(t => t.inventory),
                surfaces: ['sidebar', 'command-search'],
                to: routes.inventory.availabilityThresholds,
                accessKey: 'bizInventory.viewAvailabilityThresholds',
                hide: () => !packages.bizInventory.viewAvailabilityThresholds,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
    {
        id: 'user-platform',
        title: t(t => t.userPlatform),
        sidebarTitle: t(t => t.userPlatform),
        icon: <DevicesOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: [routes.userPlatform.base]},
        hide: () => !packages.clientPlatform.accessToMenuItems,
        children: [
            {
                id: 'user-platform-editions',
                title: tCmd(t => t.homepageEditions),
                sidebarTitle: t(t => t.homepageEditions),
                subtitle: t(t => t.userPlatform),
                surfaces: ['sidebar', 'command-search'],
                to: routes.userPlatform.list,
                sidebar: {
                    path: [
                        routes.userPlatform.list,
                        routes.userPlatform.webHomepage,
                        routes.userPlatform.phoneHomepage,
                    ],
                },
                hide: () => !(
                    packages.clientPlatform.webHomepageEditions
                    || packages.clientPlatform.phoneHomepageEditions
                ),
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'user-platform-live-homepages',
                title: tCmd(t => t.liveHomepages),
                sidebarTitle: t(t => t.liveHomepages),
                subtitle: t(t => t.userPlatform),
                surfaces: ['sidebar', 'command-search'],
                to: routes.userPlatform.liveHomepages,
                accessKey: 'clientPlatform.liveHomepages',
                hide: () => !packages.clientPlatform.liveHomepages,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'user-platform-manage-banners',
                title: tCmd(t => t.manageBanners),
                sidebarTitle: t(t => t.manageBanners),
                subtitle: t(t => t.userPlatform),
                surfaces: ['sidebar', 'command-search'],
                to: routes.userPlatform.bannerManagement,
                accessKey: 'clientPlatform.bannerManagement',
                hide: () => !packages.clientPlatform.bannerManagement,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'user-platform-manage-content',
                title: tCmd(t => t.manageUserPlatformContent),
                sidebarTitle: t(t => t.manageUserPlatformContent),
                subtitle: t(t => t.userPlatform),
                surfaces: ['sidebar', 'command-search'],
                to: routes.userPlatform.contentManagement,
                accessKey: 'clientPlatform.contentManagement',
                hide: () => !packages.clientPlatform.contentManagement,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'user-platform-surveys',
                title: tCmd(t => t.manageSurveys),
                sidebarTitle: t(t => t.manageSurveys),
                subtitle: t(t => t.userPlatform),
                surfaces: ['sidebar', 'command-search'],
                to: routes.userPlatform.surveys.list,
                sidebar: {
                    path: [
                        routes.userPlatform.surveys.base,
                        routes.userPlatform.surveys.list,
                        routes.userPlatform.surveys.surveyPreview,
                    ],
                },
                accessKey: 'clientPlatform.surveys',
                hide: () => !packages.clientPlatform.surveys,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'user-platform-dynamic-pages',
                title: tCmd(t => t.dynamicPages),
                sidebarTitle: t(t => t.dynamicPages),
                subtitle: t(t => t.userPlatform),
                surfaces: ['sidebar', 'command-search'],
                to: routes.userPlatform.dynamicPages.list,
                sidebar: {
                    path: [
                        routes.userPlatform.dynamicPages.base,
                        routes.userPlatform.dynamicPages.list,
                        routes.userPlatform.dynamicPages.single,
                    ],
                },
                accessKey: 'clientPlatform.viewDynamicPages',
                hide: () => !packages.clientPlatform.viewDynamicPages,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'user-platform-manage-color-themes',
                title: tCmd(t => t.phoneColors),
                sidebarTitle: t(t => t.phoneColors),
                subtitle: t(t => t.userPlatform),
                surfaces: ['sidebar', 'command-search'],
                to: routes.userPlatform.themeManagement.phone,
                accessKey: 'clientPlatform.themeManagement',
                hide: () => !packages.clientPlatform.themeManagement,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
    {
        id: 'ncr',
        title: t(t => t.ncr),
        sidebarTitle: t(t => t.ncr),
        icon: <BugReportOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: routes.ncr.base},
        hide: () => !packages.ncr.accessToMenuItems,
        children: [
            {
                id: 'ncr-list',
                title: tCmd(t => t.ncrList),
                sidebarTitle: t(t => t.ncrList),
                subtitle: t(t => t.ncr),
                surfaces: ['sidebar', 'command-search'],
                to: routes.ncr.reports,
                sidebar: {path: [routes.ncr.reports, routes.ncr.single]},
                accessKey: 'ncr.viewList',
                hide: () => !packages.ncr.viewList,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'ncr-notes',
                title: tCmd(t => t.ncrNotes),
                sidebarTitle: t(t => t.ncrNotes),
                subtitle: t(t => t.ncr),
                surfaces: ['sidebar', 'command-search'],
                to: routes.ncr.notes,
                accessKey: 'ncr.notes',
                hide: () => !packages.ncr.notes,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'ncr-tasks',
                title: tCmd(t => t.ncrTasks),
                sidebarTitle: t(t => t.ncrTasks),
                subtitle: t(t => t.ncr),
                surfaces: ['sidebar', 'command-search'],
                to: routes.ncr.tasks,
                accessKey: 'ncr.tasks',
                hide: () => !packages.ncr.tasks,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'ncr-settings',
                title: tCmd(t => t.ncrSettings),
                sidebarTitle: t(t => t.ncrSettings),
                subtitle: t(t => t.ncr),
                surfaces: ['sidebar', 'command-search'],
                to: routes.ncr.settings,
                accessKey: 'ncr.editConfigurations',
                hide: () => !packages.ncr.editConfigurations,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
    {
        id: 'forms-management',
        title: t(t => t.forms),
        sidebarTitle: t(t => t.forms),
        icon: <AssignmentOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: routes.forms.base},
        hide: () => !packages.bizForm.accessToMenuItems,
        children: [
            {
                id: 'forms-list',
                title: tCmd(t => t.formsList),
                sidebarTitle: t(t => t.formsList),
                subtitle: t(t => t.forms),
                surfaces: ['sidebar', 'command-search'],
                to: routes.forms.list,
                sidebar: {path: [routes.forms.list]},
                accessKey: 'bizForm.formsList',
                hide: () => !packages.bizForm.formsList,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'forms-responses',
                title: tCmd(t => t.formResponses),
                sidebarTitle: t(t => t.formsResponses),
                subtitle: t(t => t.forms),
                surfaces: ['sidebar', 'command-search'],
                to: routes.forms.responses,
                accessKey: 'bizForm.formResponsesList',
                hide: () => !packages.bizForm.formResponsesList,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
    {
        id: 'biz-point-loyalty',
        title: t(t => t.bizPointLoyalty),
        sidebarTitle: t(t => t.bizPointLoyalty),
        icon: <LoyaltyOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: routes.bizPoint.loyalty.base},
        hide: () => !(
            packages.bizPoint.loyaltyDashboard
            || packages.bizPoint.loyaltyBuyBiz
            || packages.bizPoint.loyaltyEarningRules
            || packages.bizPoint.loyaltyCustomers
            || packages.bizPoint.loyaltySettings
        ),
        children: [
            {
                id: 'biz-point-loyalty-dashboard',
                title: tCmd(t => t.bizPointLoyaltyDashboard),
                sidebarTitle: t(t => t.bizPointLoyaltyDashboard),
                subtitle: t(t => t.bizPointLoyalty),
                surfaces: ['sidebar', 'command-search'],
                to: routes.bizPoint.loyalty.dashboard,
                accessKey: 'bizPoint.loyaltyDashboard',
                hide: () => !packages.bizPoint.loyaltyDashboard,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'biz-point-loyalty-buy',
                title: tCmd(t => t.bizPointLoyaltyBuy),
                sidebarTitle: t(t => t.bizPointLoyaltyBuy),
                subtitle: t(t => t.bizPointLoyalty),
                surfaces: ['sidebar', 'command-search'],
                to: routes.bizPoint.loyalty.buy,
                accessKey: 'bizPoint.loyaltyBuyBiz',
                hide: () => !packages.bizPoint.loyaltyBuyBiz,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'biz-point-loyalty-rules',
                title: tCmd(t => t.bizPointLoyaltyRules),
                sidebarTitle: t(t => t.bizPointLoyaltyRules),
                subtitle: t(t => t.bizPointLoyalty),
                surfaces: ['sidebar', 'command-search'],
                to: routes.bizPoint.loyalty.rules,
                accessKey: 'bizPoint.loyaltyEarningRules',
                hide: () => !packages.bizPoint.loyaltyEarningRules,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'biz-point-loyalty-customers',
                title: tCmd(t => t.bizPointLoyaltyCustomers),
                sidebarTitle: t(t => t.bizPointLoyaltyCustomers),
                subtitle: t(t => t.bizPointLoyalty),
                surfaces: ['sidebar', 'command-search'],
                to: routes.bizPoint.loyalty.customers,
                accessKey: 'bizPoint.loyaltyCustomers',
                hide: () => !packages.bizPoint.loyaltyCustomers,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'biz-point-loyalty-settings',
                title: tCmd(t => t.bizPointLoyaltySettings),
                sidebarTitle: t(t => t.bizPointLoyaltySettings),
                subtitle: t(t => t.bizPointLoyalty),
                surfaces: ['sidebar', 'command-search'],
                to: routes.bizPoint.loyalty.settings,
                accessKey: 'bizPoint.loyaltySettings',
                hide: () => !packages.bizPoint.loyaltySettings,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
    {
        id: 'biz-point-admin',
        title: t(t => t.bizPointAdmin),
        sidebarTitle: t(t => t.bizPointAdmin),
        icon: <ShieldOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {path: routes.bizPoint.admin.base},
        hide: () => !(
            packages.bizPoint.adminReviewQueue
            || packages.bizPoint.adminManualAdjustment
            || packages.bizPoint.adminBusinessConfig
            || packages.bizPoint.adminLiabilityReport
            || packages.bizPoint.adminAuditLog
            || packages.bizPoint.adminPurchaseConfirm
            || packages.bizPoint.adminWalletSuspend
        ),
        children: [
            {
                id: 'biz-point-admin-review-queue',
                title: tCmd(t => t.bizPointAdminReviewQueue),
                sidebarTitle: t(t => t.bizPointAdminReviewQueue),
                subtitle: t(t => t.bizPointAdmin),
                surfaces: ['sidebar', 'command-search'],
                to: routes.bizPoint.admin.reviewQueue,
                accessKey: 'bizPoint.adminReviewQueue',
                hide: () => !packages.bizPoint.adminReviewQueue,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'biz-point-admin-purchase-confirm',
                title: tCmd(t => t.bizPointAdminPurchaseConfirm),
                sidebarTitle: t(t => t.bizPointAdminPurchaseConfirm),
                subtitle: t(t => t.bizPointAdmin),
                surfaces: ['sidebar', 'command-search'],
                to: routes.bizPoint.admin.purchaseConfirm,
                accessKey: 'bizPoint.adminPurchaseConfirm',
                hide: () => !packages.bizPoint.adminPurchaseConfirm,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'biz-point-admin-wallet-operations',
                title: tCmd(t => t.bizPointAdminWalletOperations),
                sidebarTitle: t(t => t.bizPointAdminWalletOperations),
                subtitle: t(t => t.bizPointAdmin),
                surfaces: ['sidebar', 'command-search'],
                to: routes.bizPoint.admin.walletOperations,
                accessKey: 'bizPoint.adminManualAdjustment',
                hide: () => !(
                    packages.bizPoint.adminManualAdjustment
                    || packages.bizPoint.adminWalletSuspend
                ),
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'biz-point-admin-business-config',
                title: tCmd(t => t.bizPointAdminBusinessConfig),
                sidebarTitle: t(t => t.bizPointAdminBusinessConfig),
                subtitle: t(t => t.bizPointAdmin),
                surfaces: ['sidebar', 'command-search'],
                to: routes.bizPoint.admin.businessConfig,
                accessKey: 'bizPoint.adminBusinessConfig',
                hide: () => !packages.bizPoint.adminBusinessConfig,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'biz-point-admin-liability-report',
                title: tCmd(t => t.bizPointAdminLiabilityReport),
                sidebarTitle: t(t => t.bizPointAdminLiabilityReport),
                subtitle: t(t => t.bizPointAdmin),
                surfaces: ['sidebar', 'command-search'],
                to: routes.bizPoint.admin.liabilityReport,
                accessKey: 'bizPoint.adminLiabilityReport',
                hide: () => !packages.bizPoint.adminLiabilityReport,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'biz-point-admin-audit-log',
                title: tCmd(t => t.bizPointAdminAuditLog),
                sidebarTitle: t(t => t.bizPointAdminAuditLog),
                subtitle: t(t => t.bizPointAdmin),
                surfaces: ['sidebar', 'command-search'],
                to: routes.bizPoint.admin.auditLog,
                accessKey: 'bizPoint.adminAuditLog',
                hide: () => !packages.bizPoint.adminAuditLog,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
    {
        id: 'damage-control',
        title: t(t => t.damageControl),
        sidebarTitle: t(t => t.damageControl),
        icon: <ListAltRounded/>,
        surfaces: ['sidebar'],
        sidebar: {path: routesLists.damageControl},
        hide: () => !(packages.lookup.missingPublicProductsList || packages.sales.troubleshootTransactionsList),
        children: [
            {
                id: 'damage-control-missing-products',
                title: tCmd(t => t.damageControlMissingProducts),
                sidebarTitle: t(t => t.damageControlMissingProducts),
                subtitle: t(t => t.damageControl),
                surfaces: ['sidebar', 'command-search'],
                to: routes.damageControl.missingProducts,
                accessKey: 'lookup.missingPublicProductsList',
                hide: () => !packages.lookup.missingPublicProductsList,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
            {
                id: 'damage-control-transactions',
                title: tCmd(t => t.damageControlTransactions),
                sidebarTitle: t(t => t.damageControlTransactions),
                subtitle: t(t => t.damageControl),
                surfaces: ['sidebar', 'command-search'],
                to: routes.damageControl.transactions,
                accessKey: 'sales.troubleshootTransactionsList',
                hide: () => !packages.sales.troubleshootTransactionsList,
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
    {
        id: 'support',
        title: t(t => t.support),
        sidebarTitle: t(t => t.support),
        icon: <SupportAgentOutlined/>,
        surfaces: ['sidebar'],
        sidebar: {
            path: routesLists.support,
            badge: <ContactUsMessagesUnreadBadge/>,
        },
        hide: () => !packages.support.contactUsMessages,
        children: [
            {
                id: 'support-contact-us-messages',
                title: tCmd(t => t.supportContactUsMessages),
                sidebarTitle: t(t => t.contactUsMessages),
                subtitle: t(t => t.support),
                surfaces: ['sidebar', 'command-search'],
                to: routes.support.contactUsMessages,
                accessKey: 'support.contactUsMessages',
                hide: () => !packages.support.contactUsMessages,
                sidebar: {
                    badge: <ContactUsMessagesUnreadBadge/>,
                },
                commandSearch: {providerId: 'navigation-general', category: 'pages'},
            },
        ],
    },
];

export default createOperatorPanelEntries;
